import { useCallback } from 'react';
import { DeepPartial } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { shouldRefetchQuery } from '@/graphql/client.utils';
import { useReportError } from '@/hooks/useReportError';
import {
  getHideZeroDollarItemsChanges,
  HideZeroDollarItemsSetting,
  useTrackHideZeroDollarItemsChanges,
} from '@/modules/presentation/hooks/useTrackHideZeroDollarItemsChanges';

import { useCreatePresentationTemplateMutation } from '../createPresentationTemplate/graphql/CreatePresentationTemplate.generated';
import { TenantPresentationTemplateTableDocument } from '../tenantPresentationTemplateTable/graphql/TenantPresentationTemplateTable.generated';
import { UserPresentationTemplateTableDocument } from '../userPresentationTemplateTable/graphql/UserPresentationTemplateTable.generated';
import { useUpdatePresentationTemplateMutation } from './graphql/PresentationTemplateEditor.generated';
import { usePresentationTemplateEditorContext } from './PresentationTemplateEditor.context';
import { PresentationTemplateEditorFormShape } from './PresentationTemplateEditor.types';
import {
  mapFormToCreatePresentationTemplateInput,
  mapFormToUpdatePresentationTemplateInput,
} from './PresentationTemplateEditor.utils';

interface UseHandleSubmitReturn {
  /** Updates the existing template (only valid when not in user level) */
  onUpdate: () => void;
  /** Creates a new template based on current form data, optionally overriding the display name */
  onSaveAsNew: (displayNameOverride?: string) => void;
}

// The user- and firm-level template tables are rendered on different pages
// (and neither is mounted while the editor is open), so refetching them by
// name would log "Unknown query" warnings for whichever table isn't active.
// Refetch only the currently-active tables among these instead.
const REFETCH_QUERY_DOCUMENTS = [
  UserPresentationTemplateTableDocument,
  TenantPresentationTemplateTableDocument,
];

function getHideZeroDollarItemsSettings(
  bundles: DeepPartial<PresentationTemplateEditorFormShape['bundles']> = []
): HideZeroDollarItemsSetting[] {
  return bundles.flatMap((bundle) =>
    bundle?.id && bundle.waterfallTemplateBundleConfiguration
      ? [
          {
            bundleId: bundle.id,
            hideZeroDollarItems:
              bundle.waterfallTemplateBundleConfiguration
                .summaryHideZeroDollarItems ?? false,
          },
        ]
      : []
  );
}

export function useHandleSubmit(): UseHandleSubmitReturn {
  const { onClose, onSaveComplete, createTemplateType } =
    usePresentationTemplateEditorContext();
  const { createSuccessFeedback, createErrorFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { handleSubmit, formState } =
    useFormContext<PresentationTemplateEditorFormShape>();
  const trackHideZeroDollarItemsChanges =
    useTrackHideZeroDollarItemsChanges('template');

  const afterSave = onSaveComplete ?? onClose;

  const [updatePresentationTemplate] = useUpdatePresentationTemplateMutation({
    onCompleted: () => {
      createSuccessFeedback('Presentation template saved successfully')();
      afterSave();
    },
    onError: (error) => {
      reportError('Failed to save presentation template', error);
      createErrorFeedback('Failed to save presentation template')();
    },
    refetchQueries: 'active',
    onQueryUpdated: (query) =>
      shouldRefetchQuery(query.queryName, {
        allowedQueryDocuments: REFETCH_QUERY_DOCUMENTS,
      }),
  });

  const [createPresentationTemplate] = useCreatePresentationTemplateMutation({
    onCompleted: () => {
      createSuccessFeedback('Presentation template created successfully')();
      afterSave();
    },
    onError: (error) => {
      reportError('Failed to create presentation template', error);
      createErrorFeedback('Failed to create presentation template')();
    },
    refetchQueries: 'active',
    onQueryUpdated: (query) =>
      shouldRefetchQuery(query.queryName, {
        allowedQueryDocuments: REFETCH_QUERY_DOCUMENTS,
      }),
  });

  // The form is reset from the loaded template, so its default values are the
  // saved state.
  const getChangesToTrack = useCallback(
    (bundles: PresentationTemplateEditorFormShape['bundles']) =>
      getHideZeroDollarItemsChanges(
        getHideZeroDollarItemsSettings(formState.defaultValues?.bundles),
        getHideZeroDollarItemsSettings(bundles)
      ),
    [formState.defaultValues]
  );

  const onUpdate = useCallback(() => {
    handleSubmit(async (formData) => {
      const input = mapFormToUpdatePresentationTemplateInput(formData);
      if (!input) return;

      const changes = getChangesToTrack(formData.bundles);
      const { data } = await updatePresentationTemplate({
        variables: { input },
      });
      if (data) trackHideZeroDollarItemsChanges(changes);
    })();
  }, [
    getChangesToTrack,
    handleSubmit,
    trackHideZeroDollarItemsChanges,
    updatePresentationTemplate,
  ]);

  const onSaveAsNew = useCallback(
    (displayNameOverride?: string) => {
      handleSubmit(async (formData) => {
        const input = mapFormToCreatePresentationTemplateInput(
          {
            ...formData,
            ...(displayNameOverride !== undefined && {
              displayName: displayNameOverride,
            }),
          },
          createTemplateType
        );
        const changes = getChangesToTrack(formData.bundles);
        const { data } = await createPresentationTemplate({
          variables: { input },
        });
        if (data) trackHideZeroDollarItemsChanges(changes);
      })();
    },
    [
      createPresentationTemplate,
      createTemplateType,
      getChangesToTrack,
      handleSubmit,
      trackHideZeroDollarItemsChanges,
    ]
  );

  return { onUpdate, onSaveAsNew };
}
